import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout';
import { MinimalLayoutComponent } from './layouts/minimal-layout/minimal-layout';
import { provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { userProfileReducer } from './core/user-profile/store/user-profile.reducer';
import { UserProfileEffects } from './core/user-profile/store/user-profile.effects';
import { baseLogoutRedirect } from './core/guards/base-logout.redirect';
import { adminGuard } from './core/guards/admin.guard';

export const appRoutes: Routes = [
  // Auth layout — public login entry points
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: baseLogoutRedirect,
      },
      {
        path: 'auth/callback',
        loadComponent: () =>
          import('./features/auth-callback/auth-callback.component')
            .then((m) => m.AuthCallbackComponent),
      },
      {
        path: 'login',
        title: 'Velocity | Login',
        loadComponent: () =>
          import('./features/login/login').then((m) => m.LoginComponent),
      },
      {
        path: 'invited-user',
        loadComponent: () =>
          import('./features/integrations/invited-user/invited-user.component')
            .then((m) => m.InvitedUserComponent),
      },
      {
        path: 'registration',
        title: 'Velocity | Registration',
        loadComponent: () =>
          import('./features/registration/registration').then(
            (m) => m.RegistrationComponent
          ),
      },
      {
        path: 'otp/changepassword',
        loadComponent: () =>
          import('./features/login/forgot-password/forgot-password').then(
            (m) => m.ForgotPasswordComponent
          ),
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import('./features/login/forgot-password/page/new-password/new-password').then(
            (m) => m.NewPasswordComponent
          ),
      },
    ],
  },

  // Users layout — protected user pages
  {
    path: 'user',
    title: 'Velocity | User',
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'applications',
      },
      {
        path: 'applications',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/users/applications/applications').then(
            (m) => m.UserApplicationsComponent
          ),
      },
      {
        path: 'profile',
        title: 'Velocity | User Profile',
        canActivate: [authGuard],
        providers: [
          provideState({
            name: 'userProfile',
            reducer: userProfileReducer,
          }),
          provideEffects(UserProfileEffects),
        ],
        loadComponent: () =>
          import('./core/user-profile/user-profile').then(
            (m) => m.UserProfilePage
          ),
      },
    ],
  },

  // Admin layout — protected admin pages
  {
    path: 'admin',
    title: 'Velocity | Admin',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    children: [
      {
        path: '',
        redirectTo: 'applications',
        pathMatch: 'full',
      },
      {
        path: 'identity-providers',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/identity-providers/identity-providers').then(
            (m) => m.IdentityProvidersComponent
          ),
      },
      {
        path: 'identity-providers/add',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/identity-providers/pages/add/add-identity-providers').then(
            (m) => m.AddIdentityProvidersComponent
          ),
      },
      {
        path: 'identity-providers/edit/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/identity-providers/pages/edit/edit-identity-providers').then(
            (m) => m.EditIdentityProvidersComponent
          ),
      },
      {
        path: 'organizations',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/organizations/organizations').then(
            (m) => m.OrganizationsComponent
          ),
      },
      {
        path: 'organizations/add',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/organizations/pages/add/add-organization').then(
            (m) => m.AddOrganizationComponent
          ),
      },
      {
        path: 'organizations/edit/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/organizations/pages/edit/edit-organization').then(
            (m) => m.EditOrganizationPage
          ),
      },
      {
        path: 'applications',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/applications/applications').then(
            (m) => m.ApplicationsComponent
          ),
      },
      {
        path: 'applications/add',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/applications/pages/add/add-application').then(
            (m) => m.AddApplicationComponent
          ),
      },
      {
        path: 'applications/edit/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/applications/pages/edit/edit-application').then(
            (m) => m.EditApplicationComponent
          ),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/admin/users/users').then(
            (m) => m.UsersComponent
          ),
      },
      {
        path: 'users/add',
        title: 'Velocity | Add User',
        loadComponent: () =>
          import('./features/admin/users/pages/add/add-user').then(
            (m) => m.AddUserComponent
          ),
      },
      {
        path: 'roles',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/admin/roles/roles').then(
            (m) => m.RolesComponent
          ),
      },
      {
        path: 'users/edit/:id',
        title: 'Velocity | Edit User',
        loadComponent: () =>
          import('./features/admin/users/pages/edit/edit-user').then(
            (m) => m.EditUserPage
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/admin/settings/settings').then(
            (m) => m.SettingsComponent
          ),
      },
      {
        path: 'profile',
        title: 'Velocity | User Profile',
        providers: [
          provideState({
            name: 'userProfile',
            reducer: userProfileReducer,
          }),
          provideEffects(UserProfileEffects),
        ],
        loadComponent: () =>
          import('./core/user-profile/user-profile').then(
            (m) => m.UserProfilePage
          ),
      },
    ],
  },

  // Minimal layout — error pages, logged-out screens, isolated wizards
  {
    path: '',
    component: MinimalLayoutComponent,
    children: [
      // Add dedicated error / isolated routes here
    ],
  },
];
